import {
	type GrantablePair,
	type PermissionScope,
	permissionManifest,
	scopeContains,
	scopeForGrant,
	SubjectScopeMap,
} from "@msuite/citadel";
import type { UserPermissionFormSchema } from "@msuite/utils/schemas";
// Straight from the files, not the barrel: the barrel exports React components whose import
// chain reaches the browser, and this module has to stay importable by the bun test runner.
import { grantKey } from "@/components/citadel/grant-key";
import { roleDisplayName } from "@/components/citadel/role-display-name";

/** The fields of a grant this grid reads: the pair it names and the reach it carries. */
interface PermissionGrant {
	action: string;
	subject: string;
	conditions?: Record<string, unknown> | null;
}

/** Where a role-derived grant comes from — the slice of `PermissionSource` the grid names. */
interface PermissionGrantSource {
	kind: string;
	roleName?: string;
	isSystem?: boolean;
}

/** A role-derived grant with the roles that carry it. */
interface RolePermissionGrant extends PermissionGrant {
	sources?: PermissionGrantSource[];
}

/** The slice of `GET /users/:userId/permissions/form` the grid reads. */
export interface UserPermissionsForm {
	userPermissions: PermissionGrant[];
	rolePermissions: RolePermissionGrant[];
}

/** One role granting a pair, with the reach its conditions encode — `null` for a template the
 * scope map does not know. */
export interface RoleSource {
	name: string;
	scope: PermissionScope | null;
}

/**
 * The reach a stored grant encodes, or `null` for a template outside the pair's offer — a
 * template the scope map never produced, or one whose reach the pair no longer offers. The cell
 * cannot show either as one of its choices without misstating the grant, so both render
 * read-only.
 */
function scopeOf(grant: PermissionGrant): PermissionScope | null {
	// The subject guard is load-bearing: the admin role's `manage all` row arrives here too,
	// and "all" is no scope-map subject — an unguarded lookup throws mid-render and blanks the
	// whole tab for exactly the admin-carrying users an admin is most likely to inspect.
	if (!(grant.subject in SubjectScopeMap)) return null;
	return scopeForGrant(
		{ subject: grant.subject, action: grant.action } as GrantablePair,
		grant.conditions ?? null,
	);
}

/**
 * Whether the roles' combined reach already contains a value — the relation the grid disables
 * options by, and the submit path normalises with. A custom role template (no resolvable
 * scope) is left out of the union: it cannot be compared, so it must not disable anything.
 *
 * @param union - The distinct reaches the roles grant on one pair.
 * @param value - A cell value; `false` is "no access", which any reach contains.
 * @returns `true` when granting `value` individually would add nothing.
 */
export function unionContains(union: PermissionScope[], value: false | PermissionScope): boolean {
	if (value === false) return union.length > 0;
	return union.some((scope) => scopeContains(scope, value));
}

/**
 * The distinct reaches the roles grant per pair — `organization` collapses the rest, since it
 * contains them. Two entries only ever appear for the pairs whose subject offers both narrow
 * reaches (Ticket, Project), granted by different roles.
 *
 * @param form - The `form` object returned by the permission form endpoint.
 * @returns `action:subject` → the roles' reaches, without duplicates.
 */
export function roleUnions(form: UserPermissionsForm): Record<string, PermissionScope[]> {
	const unions: Record<string, PermissionScope[]> = {};

	for (const grant of form.rolePermissions) {
		const scope = scopeOf(grant);
		if (scope === null) continue;
		const key = grantKey(grant.action, grant.subject);
		unions[key] ??= [];
		if (!unions[key].includes(scope)) unions[key].push(scope);
	}

	for (const [key, scopes] of Object.entries(unions)) {
		if (scopes.includes("organization")) unions[key] = ["organization"];
	}

	return unions;
}

/**
 * Flatten the permission form response into the two maps the grid binds to. A cell's value is
 * the user's **individual extension** — a reach the roles do not already contain — or `false`,
 * which the cell displays as whatever the roles give (the pre-selection). An individual grant
 * the roles contain seeds as `false` too: it adds nothing, is not expressible in this model,
 * and the next save normalises it away.
 *
 * @param form - The `form` object returned by the permission form endpoint.
 * @returns Form values for `userPermissionFormSchema`, keyed over the whole manifest.
 */
export function toPermissionFormValues(form: UserPermissionsForm): UserPermissionFormSchema {
	const own = new Map(
		form.userPermissions.map((grant) => [grantKey(grant.action, grant.subject), scopeOf(grant)]),
	);
	const unions = roleUnions(form);

	const permissions: UserPermissionFormSchema["permissions"] = {};

	for (const { subject, actions } of permissionManifest) {
		for (const action of actions) {
			const key = grantKey(action, subject);
			const individual = own.get(key) ?? false;
			permissions[key] =
				individual !== false && !unionContains(unions[key] ?? [], individual) ? individual : false;
		}
	}

	return { permissions };
}

/**
 * The pairs the user holds under a conditions template the scope map does not know. None are
 * produced by this write path — it only ever stores scope templates — so these are hand-written
 * rows, shown read-only as "custom" exactly like the role editor shows theirs.
 *
 * @param form - The `form` object returned by the permission form endpoint.
 * @returns The `action:subject` keys of the unrecognisable grants.
 */
export function customUserGrantKeys(form: UserPermissionsForm): Set<string> {
	return new Set(
		form.userPermissions
			.filter((grant) => scopeOf(grant) === null)
			.map((grant) => grantKey(grant.action, grant.subject)),
	);
}

/**
 * The submit payload: custom pairs stripped — their cells are read-only, and a replace-write
 * carrying them as `false` would revoke rows the form never let anyone touch — and any value
 * the roles contain normalised to `false`, so a save never plants a row that grants nothing.
 *
 * @param permissions - The form's cell map.
 * @param unions - The roles' reaches per pair, from `roleUnions`.
 * @param customKeys - The keys `customUserGrantKeys` reported.
 * @returns The map the server receives.
 */
export function toSubmitPermissions(
	permissions: UserPermissionFormSchema["permissions"],
	unions: Record<string, PermissionScope[]>,
	customKeys: Set<string>,
): UserPermissionFormSchema["permissions"] {
	return Object.fromEntries(
		Object.entries(permissions)
			.filter(([key]) => !customKeys.has(key))
			.map(([key, value]) => [key, unionContains(unions[key] ?? [], value) ? false : value]),
	);
}

/**
 * The roles granting `manage all` — the wildcard the grid cannot render cell by cell, because
 * no manifest pair carries the subject `all`. A user holding it has every permission at full
 * reach, so the grid is replaced by a note naming these roles instead of showing a hundred
 * cells that all claim "no access".
 *
 * @param form - The `form` object returned by the permission form endpoint.
 * @returns The display names of the wildcard-granting roles, without duplicates.
 */
export function wildcardRoleNames(form: UserPermissionsForm): string[] {
	return [
		...new Set(
			form.rolePermissions
				.filter((grant) => grant.subject === "all")
				.flatMap((grant) => grant.sources ?? [])
				.filter((source) => source.kind === "role" && typeof source.roleName === "string")
				.map((source) =>
					roleDisplayName({ name: source.roleName as string, isSystem: source.isSystem === true }),
				),
		),
	];
}

/**
 * The roles behind each covered pair, each with the reach its grant carries — what the open
 * dropdown annotates its pre-selection with, and the extension marker's tooltip lists. A pair
 * two roles grant at different reaches arrives as two entries and both are listed.
 *
 * @param form - The `form` object returned by the permission form endpoint.
 * @returns `action:subject` → the granting roles with their scopes, in the server's order.
 */
export function toRoleSources(form: UserPermissionsForm): Record<string, RoleSource[]> {
	const sources: Record<string, RoleSource[]> = {};

	for (const grant of form.rolePermissions) {
		const key = grantKey(grant.action, grant.subject);
		const scope = scopeOf(grant);
		for (const source of grant.sources ?? []) {
			if (source.kind !== "role" || typeof source.roleName !== "string") continue;
			sources[key] ??= [];
			sources[key].push({
				name: roleDisplayName({ name: source.roleName, isSystem: source.isSystem === true }),
				scope,
			});
		}
	}

	return sources;
}
